import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { GLabel } from '../../pdf/glabel';
import { placeObjects, PlacedObject } from '../../core/placeObjects';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/helpers';

/**
 * Lepitko (Sticker) - Sticker matching activity
 *
 * Layout: 1 portrait page. Top: 5×2 sticker grid.
 * Bottom: colorful area with randomly placed target squares showing answer text.
 *
 * Migrated from C# EduFrame.Img10.Generators.Lepitko
 */
export class Lepitko implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const page = pdf.addPage();

    const margin = PdfUnit.FromMillimeters(5);
    const baseSize = (PdfSize.A4.width - margin * 2) / 5;
    const size = { width: baseSize, height: baseSize };

    const leftTexts = opts.list.map((item) => item.left.getText() ?? '');
    const rightTexts = opts.list.map((item) => item.right.getText() ?? '');
    const leftFont = page.adjustFontMulti(
      leftTexts.map((t) => [t]),
      GUtil.baseFont,
      new PdfSize(size.width, size.height)
    );
    const rightFont = page.adjustFontMulti(
      rightTexts.map((t) => [t]),
      GUtil.baseFont,
      new PdfSize(size.width, size.height)
    );

    const restRect = new PdfRect(
      0,
      baseSize * 2 + margin * 2,
      PdfSize.A4.width,
      PdfSize.A4.height - 2 * baseSize - margin * 2
    );

    // Background with random pastel
    page.drawRectangle(
      null,
      new PdfBrush(GUtil.pastelColors[opts.data.random.next(0, GUtil.pastelColors.length)]),
      restRect
    );

    // Random pastel circles
    for (let i = 0; i < 10; i++) {
      const x = restRect.left + opts.data.random.nextDouble() * restRect.width;
      const y = restRect.top + opts.data.random.nextDouble() * restRect.height;
      const d = 120 + opts.data.random.nextDouble() * 500;
      page.drawEllipse(
        null,
        new PdfBrush(GUtil.pastelColors[opts.data.random.next(0, GUtil.pastelColors.length)]),
        new PdfRect(x - d / 2, y - d / 2, d, d)
      );
    }

    // White cover for top area
    page.drawRectangle(null, PdfBrush.White, new PdfRect(0, 0, PdfSize.A4.width, restRect.top));

    // Top sticker grid: 5×2
    for (let x = 0; x < 5; x++) {
      for (let y = 0; y < 2; y++) {
        const index = y * 5 + x;
        if (index >= opts.list.length) continue;

        const rect = new PdfRect(
          margin + size.width * x,
          margin + size.height * y,
          size.width,
          size.height
        );
        page.drawLabel(opts.list[index].left, { rect, font: leftFont });
        page.drawRectangle(GUtil.cutPen, null, rect);
      }
    }

    // Place target objects in bottom area
    const innerRect = new PdfRect(
      margin,
      baseSize * 2 + margin * 3,
      PdfSize.A4.width - 2 * margin,
      PdfSize.A4.height - baseSize * 2 - margin * 3
    );

    const rightLabelMap = new Map<string, GLabel>();
    opts.list.forEach((item) => rightLabelMap.set(item.right.getText() ?? '', item.right));

    const texts = opts.list.map((t) => t.right.getText() ?? '');
    const objSize = new PdfSize(size.width, size.height);
    const placed = placeObjects<string>(opts.data.random, innerRect, objSize, texts);

    for (const obj of placed) {
      page.drawRectangle(null, PdfBrush.White, obj.rect);
      page.drawRectangle(GUtil.basePen, null, obj.rect);
      page.drawLine(GUtil.basePen, obj.rect.topLeft, obj.rect.bottomRight);
      page.drawLine(GUtil.basePen, obj.rect.topRight, obj.rect.bottomLeft);
      if (obj.data) {
        const label = rightLabelMap.get(obj.data);
        if (label) {
          page.drawLabel(label, { rect: obj.rect, font: rightFont });
        }
      }
    }
  }
}
